/*
 * Copyright (c) 2024 Paul Ostrowski. All rights reserved.
 *
 * This software is the proprietary information of Paul Ostrowski.
 * Unauthorized copying, modification, or distribution of this code, via any medium, is strictly prohibited.
 * This code is proprietary and confidential.
 *
 * For licensing inquiries, contact Paul Ostrowski at postrowski@gmail.com.
*/

import { Open5eV2Document } from 'model/formats/Open5eV2';

// Represents anything carrying Open5e source/document info, in either:
//   - v1 shape: flat document__* fields
//   - v2 shape: a nested `document` object
// At runtime an object has exactly one of these — the helpers below resolve it.
interface Open5eDocument {
    document__slug?:        string;
    document__title?:       string;
    document__license_url?: string;
    document__url?:         string;
    document?:              Open5eV2Document;
}

export function getDocKey(item: Open5eDocument): string {
    return item.document?.key || item.document__slug || '';
}

export function getDocTitle(item: Open5eDocument): string {
    return item.document?.display_name || item.document__title || '';
}

// The v1 title of the core SRD document ('wotc-srd'); pinned to the top of source-grouped dropdowns.
export const CORE_SOURCE_NAME = '5e Core Rules';

// Natural collation, so "Tome of Beasts 2" sorts before "Tome of Beasts 11" and the "5e ..." rules
// sort ahead of everything else.
const SOURCE_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });

// Orders source display titles the way MultiSelectPopup orders its source-grouped blocks. Anything
// that has to guess a source should use this, so its guess matches what the user sees listed first.
export function compareSourceTitles(a: string, b: string): number {
    return SOURCE_COLLATOR.compare(a, b);
}

// When several sources define an identically-named entry and there's nothing to disambiguate them
// (e.g. a monster imported from open5e text names its spells but not their source), pick the one
// whose source heads the selector's list rather than whichever the API happened to return first.
// Ties keep the earliest candidate.
export function pickPreferredSource<T extends Open5eDocument>(items: T[]): T | undefined {
    return items.reduce<T | undefined>(
        (best, item) => (!best || compareSourceTitles(getDocTitle(item), getDocTitle(best)) < 0) ? item : best,
        undefined);
}

// Orders the source groups shown in the Race/Background/Monster dropdowns: drop empty groups (e.g.
// v2-only documents that carry no races/backgrounds/monsters), then put '5e Core Rules' first and
// the rest by compareSourceTitles. The raw documentSources Map order interleaves v1/v2 sources, so
// it can't be relied on for display.
export function sortSourceGroups<T>(groups: { label: string; options: T[] }[]): { label: string; options: T[] }[] {
    return groups
        .filter(group => group.options.length > 0)
        .sort((a, b) => {
            if (a.label === CORE_SOURCE_NAME) return -1;
            if (b.label === CORE_SOURCE_NAME) return 1;
            return compareSourceTitles(a.label, b.label);
        });
}

export default Open5eDocument;
